import { Link, useParams } from "react-router-dom";
import Seo from "@/lib/Seo";
import { useFetch } from "@/lib/api";
import { FadeUp } from "@/lib/motion";
import { useQuote } from "@/lib/quote";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function CollectionDetail() {
  const { slug } = useParams();
  const { data: collection, loading, error } = useFetch(`/collections/${slug}`);
  const { data: catalog } = useFetch("/catalog");
  const { addItem, setDrawerOpen } = useQuote();

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-[72px]">
        <p className="text-smoke" data-testid="collection-loading">Opening the collection…</p>
      </div>
    );
  }
  if (error || !collection) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 px-6 pt-[72px] text-center">
        <h1 className="font-display text-4xl text-ink">We could not find that collection.</h1>
        <Link to="/collections" data-testid="collection-back-link" className="rounded-none bg-olive px-8 py-3 text-[12px] uppercase tracking-[0.18em] text-ivory">
          All Collections
        </Link>
      </div>
    );
  }

  const findItem = (s) => (catalog || []).find((c) => c.slug === s);
  const addToCollection = () => {
    (collection.elements || []).forEach((e) => {
      const item = findItem(e.slug);
      addItem(item || { slug: e.slug, title: e.title, image: collection.image, unit: "Per event" });
    });
    setDrawerOpen(true);
  };

  return (
    <div className="pt-[72px]">
      <Seo
        title={`${collection.name} — Curated Wedding Collection | Marble & Veil`}
        description={collection.short.slice(0, 155)}
        path={`/collections/${collection.slug}`}
      />
      <section className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:py-20">
        <Breadcrumbs trail={[["Home", "/"], ["Collections", "/collections"], [collection.name]]} />
        <FadeUp>
          <div className="overflow-hidden bg-parch">
            <img src={collection.image} alt={`${collection.name} collection concept — design inspiration`} className="aspect-[21/9] w-full object-cover" width="1600" height="686" data-testid="collection-hero-image" />
          </div>
        </FadeUp>
        <div className="mt-12 grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <FadeUp>
              <h1 className="font-display text-4xl leading-[1.08] text-ink sm:text-5xl" data-testid="collection-title">{collection.name}</h1>
              <p className="mt-2 font-display text-2xl italic text-olive">{collection.short}</p>
              <p className="mt-6 leading-relaxed text-smoke">{collection.description}</p>
              <p className="mt-4 text-[12px] leading-relaxed text-smoke" data-testid="collection-note">{collection.note}</p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <h2 className="mt-12 font-display text-3xl text-ink">In this concept</h2>
              <ul className="mt-6 divide-y divide-ink/10 border-y border-ink/10" data-testid="collection-elements">
                {(collection.elements || []).map((e) => {
                  const item = findItem(e.slug);
                  return (
                    <li key={e.slug} className="flex items-center justify-between gap-4 py-4">
                      <span className="text-ink">
                        {item ? <Link to={`/shop/${e.slug}`} className="transition-colors hover:text-olive">{e.title}</Link> : e.title}
                      </span>
                      <button
                        data-testid={`collection-add-${e.slug}`}
                        onClick={() => addItem(item || { slug: e.slug, title: e.title, image: collection.image, unit: "Per event" })}
                        className="flex-none border border-ink/20 px-5 py-2 text-[11px] uppercase tracking-[0.16em] text-ink transition-colors hover:border-olive hover:bg-olive hover:text-ivory"
                      >
                        Add to My Quote
                      </button>
                    </li>
                  );
                })}
              </ul>
              {(collection.optional || []).length > 0 && (
                <>
                  <p className="mv-label mt-8">Optional additions</p>
                  <ul className="mt-3 space-y-1.5 text-sm text-smoke" data-testid="collection-optionals">
                    {collection.optional.map((o) => (
                      <li key={o.slug} className="flex items-baseline gap-3">
                        <span className="h-px w-5 flex-none bg-olive" aria-hidden="true" /> {o.title}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </FadeUp>
          </div>

          <div className="lg:col-span-5">
            <FadeUp delay={0.15}>
              <div className="border border-ink/10 bg-parch p-8 lg:sticky lg:top-28">
                <h2 className="font-display text-2xl text-ink">Build it together</h2>
                <p className="mt-3 text-sm leading-relaxed text-smoke">
                  Add the whole concept to your quote list as a starting point, then refine it with us.
                  Elements, quantities, and the palette are all decided together — pricing is confirmed
                  in your proposal.
                </p>
                <button
                  data-testid="collection-build-button"
                  onClick={addToCollection}
                  className="mt-6 w-full rounded-none bg-olive py-4 text-[12px] uppercase tracking-[0.18em] text-ivory transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Build My Collection
                </button>
                <Link to="/quote" data-testid="collection-quote-link" className="mt-3 block border border-ink/20 py-4 text-center text-[12px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-olive hover:text-olive">
                  Go to My Quote
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
